import { SearchOutlined } from '@ant-design/icons';
import { Input, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  RepeatableColumn,
  SelectOption,
  WorkflowFieldPropertiesPayload,
  WorkflowFieldType,
} from '@/api/workflows';

import { metaForFieldType } from '../fieldMeta';

const { Text } = Typography;

const CARD_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff',
  textAlign: 'left',
  width: '100%',
  cursor: 'pointer',
  transition: 'border-color 140ms ease, background 140ms ease, box-shadow 140ms ease',
};

const ICON_TILE: CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 7,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 15,
  flexShrink: 0,
};

export type CatalogFieldDefinition = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr: string;
  required: boolean;
  properties: WorkflowFieldPropertiesPayload;
};

// ---- shared property shapes -------------------------------------------------

const TEXT = (maxLength = 200): WorkflowFieldPropertiesPayload => ({ maxLength });
const SIN: WorkflowFieldPropertiesPayload = {
  maxLength: 11,
  placeholder: 'XXX XXX XXX',
  validate: 'sin',
};
const POSTAL: WorkflowFieldPropertiesPayload = {
  maxLength: 7,
  placeholder: 'A1A 1A1',
  validate: 'postal',
};
const PHONE: WorkflowFieldPropertiesPayload = {
  maxLength: 20,
  placeholder: '(xxx) xxx-xxxx',
  validate: 'phone',
};
const DATE_PROPS: WorkflowFieldPropertiesPayload = { hintEn: 'DD/MM/YYYY', hintFr: 'JJ/MM/AAAA' };
const CURRENCY: WorkflowFieldPropertiesPayload = { min: 0, step: 0.01, numberFormat: 'currency' };

const opt = (value: string, labelEn: string, labelFr: string): SelectOption => ({
  value,
  labelEn,
  labelFr,
});

const YES_NO_OPTIONS: WorkflowFieldPropertiesPayload = {
  options: [opt('yes', 'Yes', 'Oui'), opt('no', 'No', 'Non')],
};

function f(
  id: string,
  type: WorkflowFieldType,
  labelEn: string,
  labelFr: string,
  required: boolean,
  properties: WorkflowFieldPropertiesPayload,
): CatalogFieldDefinition {
  return { id, type, labelEn, labelFr, required, properties };
}

function col(
  type: WorkflowFieldType,
  labelEn: string,
  labelFr: string,
  options: SelectOption[] = [],
): RepeatableColumn {
  return { id: crypto.randomUUID(), type, labelEn, labelFr, required: false, options };
}

function repeatable(
  id: string,
  labelEn: string,
  labelFr: string,
  columns: RepeatableColumn[],
): CatalogFieldDefinition {
  return {
    id,
    type: 'RepeatableRow',
    labelEn,
    labelFr,
    required: false,
    properties: { repeatableTemplate: columns },
  };
}

/**
 * Static field catalog seeded from the real AcroForm fields of BMO Form 2002
 * (Education Savings Plan – Advisor application), extracted with pypdf from
 * `40069_BMO_2002 EN_Fillable(Nov 20,23)_V1.pdf`. No backend endpoint exists
 * for a curated catalog yet, so this is hardcoded until that lands.
 *
 * Repeating column groups on the PDF (fund tables, PAC rows, Family Plan
 * beneficiary rows) are consolidated into single RepeatableRow entries rather
 * than one flat field per numbered instance. Y/M/D date triplets are
 * consolidated into one Date field. RESET/Print are PDF pushbuttons, not data
 * fields, and are excluded. Each entry carries the source AcroField name(s) as
 * a comment for traceability — there's no real pdfEntryId yet to wire actual
 * pdfMappings against.
 */

// ---- Section 1: Subscriber / Joint Subscriber -------------------------------

const SUBSCRIBER_FIELDS: CatalogFieldDefinition[] = [
  // AcroField: Representative
  f(
    'representative-number',
    'ShortText',
    'Representative Number',
    'Numéro du représentant',
    false,
    TEXT(50),
  ),
  // AcroFields: PlanEndDateYear, PlanEndDateMonth, PlanEndDateDay
  f('plan-end-date', 'Date', 'Plan End Date', 'Date de fin du régime', true, DATE_PROPS),
  // AcroField: Radio#1
  f('subscriber-title', 'RadioGroup', 'Title', 'Titre', true, {
    options: [
      opt('mr', 'Mr.', 'M.'),
      opt('mrs', 'Mrs.', 'Mme'),
      opt('ms', 'Ms.', 'Mme'),
      opt('dr', 'Dr.', 'Dr'),
    ],
  }),
  // AcroField: Radio#2
  f('plan-type', 'RadioGroup', 'Plan Type', 'Type de régime', true, {
    options: [
      opt('individual', 'Individual Plan', 'Régime individuel'),
      opt('family', 'Family Plan', 'Régime familial'),
    ],
  }),
  // AcroField: Radio#3
  f('correspondence-language', 'RadioGroup', 'Language', 'Langue', true, {
    options: [opt('english', 'English', 'Anglais'), opt('french', 'French', 'Français')],
  }),
  // AcroField: InsuredLastName
  f(
    'subscriber-last-name',
    'ShortText',
    'Subscriber Last Name',
    'Nom de famille du souscripteur',
    true,
    TEXT(),
  ),
  // AcroField: InsuredFirstName
  f(
    'subscriber-first-name',
    'ShortText',
    'Subscriber First Name',
    'Prénom du souscripteur',
    true,
    TEXT(),
  ),
  // AcroField: InsuredInitials
  f(
    'subscriber-initials',
    'ShortText',
    'Subscriber Initials',
    'Initiales du souscripteur',
    false,
    TEXT(10),
  ),
  // AcroField: InsuredAddress
  f(
    'subscriber-address',
    'ShortText',
    'Subscriber Address',
    'Adresse du souscripteur',
    true,
    TEXT(),
  ),
  // AcroField: InsuredApartmentNumber
  f(
    'subscriber-apartment-number',
    'ShortText',
    'Subscriber Apartment Number',
    "Numéro d'appartement du souscripteur",
    false,
    TEXT(20),
  ),
  // AcroField: InsuredCity
  f('subscriber-city', 'ShortText', 'Subscriber City', 'Ville du souscripteur', true, TEXT(80)),
  // AcroField: InsuredProvince
  f(
    'subscriber-province',
    'ShortText',
    'Subscriber Province',
    'Province du souscripteur',
    true,
    TEXT(40),
  ),
  // AcroField: InsuredPostalCode
  f(
    'subscriber-postal-code',
    'ShortText',
    'Subscriber Postal Code',
    'Code postal du souscripteur',
    true,
    POSTAL,
  ),
  // AcroField: InsuredResidenceTelephone
  f(
    'subscriber-residence-telephone',
    'ShortText',
    'Subscriber Residence Telephone',
    'Téléphone (résidence) du souscripteur',
    false,
    PHONE,
  ),
  // AcroField: InsuredBusinessTelephone
  f(
    'subscriber-business-telephone',
    'ShortText',
    'Subscriber Business Telephone',
    'Téléphone (bureau) du souscripteur',
    false,
    PHONE,
  ),
  // AcroField: InsuredSIN
  f(
    'subscriber-sin',
    'ShortText',
    'Subscriber Social Insurance Number',
    "Numéro d'assurance sociale du souscripteur",
    true,
    SIN,
  ),
  // AcroField: InsuredBN
  f(
    'subscriber-bn',
    'ShortText',
    'Subscriber Business Number (if applicable)',
    "Numéro d'entreprise du souscripteur (le cas échéant)",
    false,
    TEXT(20),
  ),
  // AcroFields: InsuredDateOfBirthYear, InsuredDateOfBirthMonth, InsuredDateOfBirthDay
  f(
    'subscriber-date-of-birth',
    'Date',
    'Subscriber Date of Birth',
    'Date de naissance du souscripteur',
    true,
    DATE_PROPS,
  ),
  // AcroField: Radio#4
  f(
    'joint-subscriber-title',
    'RadioGroup',
    'Joint Subscriber Title',
    'Titre du souscripteur conjoint',
    false,
    {
      options: [
        opt('mr', 'Mr.', 'M.'),
        opt('mrs', 'Mrs.', 'Mme'),
        opt('ms', 'Ms.', 'Mme'),
        opt('dr', 'Dr.', 'Dr'),
      ],
    },
  ),
  // AcroField: JointInsuredSIN
  f(
    'joint-subscriber-sin',
    'ShortText',
    'Joint Subscriber Social Insurance Number',
    "Numéro d'assurance sociale du souscripteur conjoint",
    false,
    SIN,
  ),
  // AcroFields: JointInsuredDateOfBirthYear, JointInsuredDateOfBirthMonth, JointInsuredDateOfBirthDay
  f(
    'joint-subscriber-date-of-birth',
    'Date',
    'Joint Subscriber Date of Birth',
    'Date de naissance du souscripteur conjoint',
    false,
    DATE_PROPS,
  ),
  // AcroField: JointInsuredLastName
  f(
    'joint-subscriber-last-name',
    'ShortText',
    'Joint Subscriber Last Name',
    'Nom de famille du souscripteur conjoint',
    false,
    TEXT(),
  ),
  // AcroField: JointInsuredFirstName
  f(
    'joint-subscriber-first-name',
    'ShortText',
    'Joint Subscriber First Name',
    'Prénom du souscripteur conjoint',
    false,
    TEXT(),
  ),
  // AcroField: JointInsuredInitials
  f(
    'joint-subscriber-initials',
    'ShortText',
    'Joint Subscriber Initials',
    'Initiales du souscripteur conjoint',
    false,
    TEXT(10),
  ),
  // AcroField: JointInsuredAddress
  f(
    'joint-subscriber-address',
    'ShortText',
    'Joint Subscriber Address',
    'Adresse du souscripteur conjoint',
    false,
    TEXT(),
  ),
  // AcroField: JointInsuredApartmentNumber
  f(
    'joint-subscriber-apartment-number',
    'ShortText',
    'Joint Subscriber Apartment Number',
    "Numéro d'appartement du souscripteur conjoint",
    false,
    TEXT(20),
  ),
  // AcroField: JointInsuredCity
  f(
    'joint-subscriber-city',
    'ShortText',
    'Joint Subscriber City',
    'Ville du souscripteur conjoint',
    false,
    TEXT(80),
  ),
  // AcroField: JointInsuredProvince
  f(
    'joint-subscriber-province',
    'ShortText',
    'Joint Subscriber Province',
    'Province du souscripteur conjoint',
    false,
    TEXT(40),
  ),
  // AcroField: JointInsuredPostalCode
  f(
    'joint-subscriber-postal-code',
    'ShortText',
    'Joint Subscriber Postal Code',
    'Code postal du souscripteur conjoint',
    false,
    POSTAL,
  ),
];

// ---- Section 2: Beneficiary Information -------------------------------------

const BENEFICIARY_FIELDS: CatalogFieldDefinition[] = [
  // AcroField: SiblingsOnlyFamilyPlan
  f(
    'siblings-only-family-plan',
    'Checkbox',
    'Siblings Only Family Plan',
    'Régime familial de frères et sœurs seulement',
    false,
    {},
  ),
  // AcroField: IndividualPlanSurName
  f(
    'beneficiary-surname',
    'ShortText',
    'Beneficiary Surname',
    'Nom de famille du bénéficiaire',
    true,
    TEXT(),
  ),
  // AcroField: IndividualPlanFirstName
  f(
    'beneficiary-first-name',
    'ShortText',
    'Beneficiary First Name',
    'Prénom du bénéficiaire',
    true,
    TEXT(),
  ),
  // AcroField: Radio#5
  f('beneficiary-gender', 'RadioGroup', 'Beneficiary Gender', 'Genre du bénéficiaire', true, {
    options: [
      opt('male', 'Male', 'Homme'),
      opt('female', 'Female', 'Femme'),
      opt('another', 'Another gender', 'Un autre genre'),
    ],
  }),
  // AcroFields: IndividualPlanDateOfBirthYear, IndividualPlanDateOfBirthMonth, IndividualPlanDateOfBirthDay
  f(
    'beneficiary-date-of-birth',
    'Date',
    'Beneficiary Date of Birth',
    'Date de naissance du bénéficiaire',
    true,
    DATE_PROPS,
  ),
  // AcroField: IndividualPlanSIN
  f(
    'beneficiary-sin',
    'ShortText',
    'Beneficiary Social Insurance Number',
    "Numéro d'assurance sociale du bénéficiaire",
    true,
    SIN,
  ),
  // AcroField: IndividualPlanAddress
  f(
    'beneficiary-address',
    'ShortText',
    'Beneficiary Street Address (if different from above)',
    'Adresse du bénéficiaire (si différente de celle ci-dessus)',
    false,
    TEXT(),
  ),
  // AcroField: IndividualPlanCity
  f('beneficiary-city', 'ShortText', 'Beneficiary City', 'Ville du bénéficiaire', true, TEXT(80)),
  // AcroField: IndividualPlanProvince
  f(
    'beneficiary-province',
    'ShortText',
    'Beneficiary Province',
    'Province du bénéficiaire',
    true,
    TEXT(40),
  ),
  // AcroField: IndividualPlanPostalCode
  f(
    'beneficiary-postal-code',
    'ShortText',
    'Beneficiary Postal Code',
    'Code postal du bénéficiaire',
    true,
    POSTAL,
  ),
  // AcroField: IndividualPlanRelationshipToInsured
  f(
    'beneficiary-relationship-to-subscriber',
    'ShortText',
    'Beneficiary Relationship to Subscriber(s)',
    'Lien de parenté du bénéficiaire avec le(s) souscripteur(s)',
    true,
    TEXT(),
  ),
  // AcroField: IndividualPlanPrimaryCaregiver
  f(
    'beneficiary-primary-caregiver',
    'ShortText',
    'Parent/Guardian or Public Primary Caregiver',
    'Parent/tuteur ou aidant naturel public principal',
    false,
    TEXT(),
  ),
  // AcroField: IndividualPlanPrimaryCaregiverAddress
  f(
    'beneficiary-primary-caregiver-address',
    'ShortText',
    'Address of Parent/Guardian or Public Primary Caregiver',
    "Adresse du parent/tuteur ou de l'aidant naturel public principal",
    false,
    TEXT(),
  ),
  // AcroFields: FamilyPlanSurName#1-2, FamilyPlanFirstName#1-2, Radio#6/#7 (gender),
  // FamilyPlanDateOfBirthYear/Month/Day#1-2, FamilyPlanSIN#1-2, FamilyPlanAddress#1-2,
  // FamilyPlanCity#1-2, FamilyPlanProvince#1-2, FamilyPlanPostalCode#1-2,
  // FamilyPlanRelationshipToInsured#1-2, FamilyPlanPrimaryCaregiver#1-2,
  // FamilyPlanAddressPrimaryCaregiver#1-2 (2 rows)
  repeatable(
    'family-plan-beneficiaries',
    'Family Plan Beneficiaries',
    'Bénéficiaires du régime familial',
    [
      col('ShortText', 'Surname', 'Nom de famille'),
      col('ShortText', 'First Name', 'Prénom'),
      col('RadioGroup', 'Gender', 'Genre', [
        opt('male', 'Male', 'Homme'),
        opt('female', 'Female', 'Femme'),
        opt('another', 'Another gender', 'Un autre genre'),
      ]),
      col('Date', 'Date of Birth', 'Date de naissance'),
      col('ShortText', 'Social Insurance Number', "Numéro d'assurance sociale"),
      col('ShortText', 'Address', 'Adresse'),
      col('ShortText', 'City', 'Ville'),
      col('ShortText', 'Province', 'Province'),
      col('ShortText', 'Postal Code', 'Code postal'),
      col(
        'ShortText',
        'Relationship to Subscriber(s)',
        'Lien de parenté avec le(s) souscripteur(s)',
      ),
      col(
        'ShortText',
        'Parent/Guardian or Public Primary Caregiver',
        'Parent/tuteur ou aidant naturel public principal',
      ),
      col(
        'ShortText',
        'Address of Parent/Guardian or Public Primary Caregiver',
        "Adresse du parent/tuteur ou de l'aidant naturel public principal",
      ),
    ],
  ),
  // AcroField: FamilyPlanCheckA
  f('schedule-a-attached', 'Checkbox', 'Schedule A Attached', 'Annexe A jointe', false, {}),
  // AcroField: Check Box9 — unlabeled/auto-named checkbox in the source PDF; exact
  // purpose couldn't be confirmed from the extracted text alone.
  f(
    'confirmation-checkbox',
    'Checkbox',
    'Confirmation Checkbox',
    'Case de confirmation',
    false,
    {},
  ),
];

// ---- Section 3-4: Applying for Grants / Designated Educational Institution --

const GRANTS_FIELDS: CatalogFieldDefinition[] = [
  // AcroField: Radio#8
  f(
    'apply-for-grants-on-behalf',
    'RadioGroup',
    'Apply for Grants/CLB on Your Behalf',
    'Demande de subventions/BEC en votre nom',
    false,
    YES_NO_OPTIONS,
  ),
  // AcroField: Radio#9
  f(
    'applying-for-cesg',
    'RadioGroup',
    'Applying for CESG',
    'Demande de SCEE',
    false,
    YES_NO_OPTIONS,
  ),
  // AcroField: Radio#11
  f(
    'applying-for-additional-cesg',
    'RadioGroup',
    'Applying for Additional CESG',
    'Demande de SCEE supplémentaire',
    false,
    YES_NO_OPTIONS,
  ),
  // AcroField: Radio#10
  f('applying-for-clb', 'RadioGroup', 'Applying for CLB', 'Demande de BEC', false, YES_NO_OPTIONS),
  // AcroField: FundName
  f('clb-fund-name', 'ShortText', 'CLB Fund Name', 'Nom du fonds (BEC)', false, TEXT()),
  // AcroField: FundNumber
  f('clb-fund-number', 'ShortText', 'CLB Fund Number', 'Numéro du fonds (BEC)', false, TEXT(20)),
  // AcroField: Radio#12
  f(
    'custodial-parent-and-primary-caregiver',
    'RadioGroup',
    'Custodial Parent/Legal Guardian and Primary Caregiver',
    'Parent ayant la garde/tuteur légal et aidant naturel principal',
    false,
    YES_NO_OPTIONS,
  ),
  // AcroField: Radio#13
  f(
    'applying-for-provincial-grant',
    'RadioGroup',
    'Applying for Provincial Grant',
    'Demande de subvention provinciale',
    false,
    YES_NO_OPTIONS,
  ),
  // AcroField: Provincial Grant Province
  f(
    'provincial-grant-province',
    'ShortText',
    'Provincial Grant Province',
    'Province de la subvention provinciale',
    false,
    TEXT(40),
  ),
  // AcroField: ProvincialIncentiveFundName
  f(
    'provincial-incentive-fund-name',
    'ShortText',
    'Provincial Incentive Fund Name',
    'Nom du fonds (incitatif provincial)',
    false,
    TEXT(),
  ),
  // AcroField: ProvincialIncentiveFundNumber
  f(
    'provincial-incentive-fund-number',
    'ShortText',
    'Provincial Incentive Fund Number',
    'Numéro du fonds (incitatif provincial)',
    false,
    TEXT(20),
  ),
  // AcroField: NameOfInstitution
  f(
    'designated-institution-name',
    'ShortText',
    'Designated Educational Institution Name',
    "Nom de l'établissement d'enseignement désigné",
    false,
    TEXT(),
  ),
];

// ---- Section 5: Investment Instructions -------------------------------------

const INVESTMENT_FIELDS: CatalogFieldDefinition[] = [
  // AcroField: IncomingRESP
  f(
    'incoming-resp',
    'Checkbox',
    'Incoming RESP (Transfer Form Attached)',
    'REEE entrant (formulaire de transfert joint)',
    false,
    {},
  ),
  // AcroField: Radio#14
  f('investment-payment-method', 'RadioGroup', 'Payment Method', 'Mode de paiement', false, {
    options: [
      opt('cheque', 'Cheque', 'Chèque'),
      opt('internal-transfer', 'Internal Transfer', 'Transfert interne'),
    ],
  }),
  // AcroField: ChequeAmount
  f('cheque-amount', 'Number', 'Cheque Amount', 'Montant du chèque', false, CURRENCY),
  // AcroField: InternalTransferAccount
  f(
    'internal-transfer-account',
    'ShortText',
    'Internal Transfer Account Number',
    'Numéro de compte de transfert interne',
    false,
    TEXT(40),
  ),
  // AcroFields: FundNameInvestment#1-5, SalesCharge#1-5, FundNumber#1-5,
  // Amount$or%#1-5, WireOrderNumber#1-5 (5 rows)
  repeatable(
    'investment-instructions-funds',
    'Investment Instructions – Funds',
    'Directives de placement – Fonds',
    [
      col('ShortText', 'Fund Name', 'Nom du fonds'),
      col('Number', 'Sales Charge Option Front End %', 'Option de frais de vente initiaux (%)'),
      col('ShortText', 'Fund Number', 'Numéro du fonds'),
      col('ShortText', 'Amount ($ or %)', 'Montant ($ ou %)'),
      col('ShortText', 'Wire Order Number', 'Numéro de virement'),
    ],
  ),
  // AcroField: Amount$or%Total
  f(
    'investment-total-amount',
    'Number',
    'Total Investment Amount',
    'Montant total du placement',
    false,
    CURRENCY,
  ),
];

// ---- Section 6: BMO Mutual Funds Dollar Cost Averaging (DCA) ----------------

const DCA_FIELDS: CatalogFieldDefinition[] = [
  // AcroField: Fund Name (choice field)
  f('dca-from-fund', 'SingleSelect', 'DCA From Fund', 'PAM – Fonds de provenance', false, {
    placeholder: 'Select a fund',
    options: [
      opt('bmo-money-market-fund', 'BMO Money Market Fund', 'Fonds du marché monétaire BMO'),
      opt(
        'bmo-mortgage-short-term-income-fund',
        'BMO Mortgage and Short Term Income Fund',
        'Fonds hypothécaire et de revenu à court terme BMO',
      ),
      opt('bmo-core-plus-bond-fund', 'BMO Core Plus Bond Fund', 'Fonds d’obligations Plus BMO'),
      opt(
        'bmo-fixed-income-etf-portfolio',
        'BMO Fixed Income ETF Portfolio',
        'Portefeuille de FNB à revenu fixe BMO',
      ),
      opt(
        'bmo-ultra-short-term-bond-etf-fund',
        'BMO Ultra Short-Term Bond ETF Fund',
        'Fonds négocié en bourse d’obligations à très court terme BMO',
      ),
    ],
  }),
  // AcroField: FromFundNumber
  f(
    'dca-from-fund-number',
    'ShortText',
    'DCA From Fund Number',
    'Numéro du fonds de provenance (PAM)',
    false,
    TEXT(20),
  ),
  // AcroField: Radio#15
  f('dca-amount-type', 'RadioGroup', 'DCA Amount Type', 'Type de montant (PAM)', false, {
    options: [opt('dollar', 'Dollar', 'Dollar'), opt('percentage', 'Percentage', 'Pourcentage')],
  }),
  // AcroFields: ToFundName#1-5, ToFundNumber#1-5, ToFundAmount#1-5 (5 rows)
  repeatable('dca-to-funds', 'DCA – To Funds', 'PAM – Fonds de destination', [
    col('ShortText', 'Fund Name', 'Nom du fonds'),
    col('ShortText', 'Fund Number', 'Numéro du fonds'),
    col('Number', 'Amount', 'Montant'),
  ]),
  // AcroField: Radio#16
  f('dca-frequency', 'RadioGroup', 'DCA Frequency', 'Fréquence (PAM)', false, {
    options: [
      opt('weekly', 'Weekly', 'Hebdomadaire'),
      opt('bi-weekly', 'Bi-Weekly', 'Aux deux semaines'),
      opt('monthly', 'Monthly', 'Mensuel'),
      opt('bi-monthly', 'Bi-Monthly', 'Bimestriel'),
      opt('quarterly', 'Quarterly', 'Trimestriel'),
      opt('semi-annually', 'Semi-Annually', 'Semestriel'),
      opt('annually', 'Annually', 'Annuel'),
    ],
  }),
  // AcroFields: StartDateYear, StartDateMonth, StartDateDay
  f('dca-start-date', 'Date', 'DCA Start Date', 'Date de début (PAM)', false, DATE_PROPS),
  // AcroFields: EndDateYear, EndDateMonth, EndDateDay
  f('dca-end-date', 'Date', 'DCA End Date', 'Date de fin (PAM)', false, DATE_PROPS),
];

// ---- Section 8: BMO Mutual Funds Pre-Authorized Chequing Plan (PAC) --------

const PAC_FIELDS: CatalogFieldDefinition[] = [
  // AcroFields: PACFundName#1-3, PACFundNumber#1-3, PACFundAmount#1-3,
  // PACFundStartDateYear/Month/Day#1-3, Radio#17/#18/#19 (frequency, 3 rows)
  repeatable('pac-funds', 'PAC – Funds', 'RPA – Fonds', [
    col('ShortText', 'Fund Name', 'Nom du fonds'),
    col('ShortText', 'Fund Number', 'Numéro du fonds'),
    col('Number', 'Amount', 'Montant'),
    col('Date', 'Start Date', 'Date de début'),
    col('RadioGroup', 'Frequency', 'Fréquence', [
      opt('one-time', 'One-Time', 'Paiement unique'),
      opt('weekly', 'Weekly', 'Hebdomadaire'),
      opt('bi-weekly', 'Bi-Weekly', 'Aux deux semaines'),
      opt('monthly', 'Monthly', 'Mensuel'),
      opt('bi-monthly', 'Bi-Monthly', 'Bimestriel'),
      opt('quarterly', 'Quarterly', 'Trimestriel'),
      opt('semi-annually', 'Semi-Annually', 'Semestriel'),
      opt('annually', 'Annually', 'Annuel'),
    ]),
  ]),
  // AcroField: BMOInvestmentsAuthorizing
  f(
    'pac-financial-institution-name',
    'ShortText',
    'Name of Financial Institution',
    "Nom de l'institution financière",
    false,
    TEXT(),
  ),
  // AcroField: BranchAddress
  f('pac-branch-address', 'ShortText', 'Branch Address', 'Adresse de la succursale', false, TEXT()),
  // AcroField: TransitNumber
  f('pac-transit-number', 'ShortText', 'Transit Number', 'Numéro de transit', false, TEXT(10)),
  // AcroField: InstitutionNumber
  f(
    'pac-institution-number',
    'ShortText',
    'Institution Number',
    "Numéro d'institution",
    false,
    TEXT(10),
  ),
  // AcroField: AccountNumber
  f(
    'pac-account-number',
    'ShortText',
    'Bank Account Number',
    'Numéro de compte bancaire',
    false,
    TEXT(20),
  ),
];

// ---- Section 7 & 9: Special Instructions / Signature ------------------------

const MISC_FIELDS: CatalogFieldDefinition[] = [
  // AcroField: SpecialInstructions
  f(
    'special-instructions',
    'LongText',
    'Special Instructions',
    'Instructions particulières',
    false,
    { maxLength: 2000 },
  ),
  // AcroField: RepresentativeName
  f(
    'representative-name',
    'ShortText',
    'Representative Name',
    'Nom du représentant',
    false,
    TEXT(),
  ),
  // AcroField: Dealer
  f('dealer-number', 'ShortText', 'Dealer Number', 'Numéro du courtier', false, TEXT(20)),
  // AcroField: AccountNumber-MF
  f(
    'mutual-fund-account-number',
    'ShortText',
    'Mutual Fund Account Number',
    'Numéro de compte de fonds communs',
    false,
    TEXT(20),
  ),
  // Note: RESET and Print AcroFields are PDF pushbuttons (form-level actions),
  // not data-capturing fields, and are intentionally excluded.
];

export const CATALOG_FIELDS: CatalogFieldDefinition[] = [
  ...SUBSCRIBER_FIELDS,
  ...BENEFICIARY_FIELDS,
  ...GRANTS_FIELDS,
  ...INVESTMENT_FIELDS,
  ...DCA_FIELDS,
  ...PAC_FIELDS,
  ...MISC_FIELDS,
];

/**
 * "Catalog" tab body for the field palette — a predefined/curated list of
 * fields (seeded from a real BMO application PDF's AcroForm fields), distinct
 * from the "Custom" tab's user-saved elements. Cards behave exactly like the
 * Palette tab's own field cards: click to arm, click an empty canvas column
 * to place.
 */
export function CatalogTabContent({
  armedFieldId,
  onPickField,
}: {
  armedFieldId?: string | null;
  onPickField: (entry: CatalogFieldDefinition) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return CATALOG_FIELDS;
    return CATALOG_FIELDS.filter(
      (entry) =>
        entry.labelEn.toLowerCase().includes(needle) ||
        entry.labelFr.toLowerCase().includes(needle),
    );
  }, [search]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Input
        placeholder={t('workflow.customElements.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        size="small"
        suffix={<SearchOutlined style={{ color: '#6b7280' }} />}
      />

      {filtered.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12, padding: '0 4px' }}>
          {t('workflow.customElements.empty')}
        </Text>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.map((entry) => (
          <CatalogFieldCard
            key={entry.id}
            entry={entry}
            armed={armedFieldId === entry.id}
            onClick={() => onPickField(entry)}
          />
        ))}
      </div>
    </div>
  );
}

function CatalogFieldCard({
  entry,
  armed,
  onClick,
}: {
  entry: CatalogFieldDefinition;
  armed: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const meta = metaForFieldType(entry.type);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...CARD_BASE,
        background: armed ? meta.tintBg : '#fff',
        borderColor: armed ? meta.tintFg : '#e5e7eb',
        boxShadow: armed
          ? `0 2px 6px ${meta.tintBg}, 0 0 0 1px ${meta.tintFg}`
          : '0 1px 2px rgba(0, 0, 0, 0.03)',
      }}
    >
      <span style={{ ...ICON_TILE, background: meta.tintBg, color: meta.tintFg }}>
        {meta.icon()}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ fontSize: 13, color: '#111827', display: 'block' }} ellipsis>
          {entry.labelEn}
        </Text>
        <Text style={{ fontSize: 11, color: '#475569' }}>{t(meta.labelKey)}</Text>
      </div>
    </button>
  );
}
